import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {addFavoriteEstate, removeFavoriteEstate} from 'shared/api/api';
import {useTranslation} from 'react-i18next'; // Add this import
import {formatPriceSummary} from 'shared/utils/currency';


const SingleEstate = ({estate, onClick}) => {
    const dispatch = useDispatch();
    const [isFavoriting, setIsFavoriting] = useState(false);
    const favorites = useSelector((state) => state.auth.favorites) || [];
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const isFavorited = favorites.some(fav => fav.estate_id === estate.id);

    const {i18n} = useTranslation(); // Add this hook
    const currentLocale = i18n.language;

    // Function to get the correct title based on locale
    const getLocalizedTitle = () => {

        // If the estate has _locales and a translation exists for current locale
        if (estate._locales && estate._locales[currentLocale]) {
            return estate._locales[currentLocale].title;
        }
        // Fallback to default title
        return estate.title;
    };

    // Get the appropriate thumbnail image (watermarked if available)
    const getThumbnail = () => {
        // Use watermarked thumbnail if available
        if (estate.thumbs) {
            return estate.thumbscd ;
        }
        // Fallback to regular thumbnail
        return estate.thumb;
    };

    const handleFavoriteClick = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (isFavoriting || !isAuthenticated) return;

        try {
            setIsFavoriting(true);
            if (isFavorited) {
                const favoriteId = favorites.find(fav => fav.estate_id === estate.id).id;
                const {data} = await removeFavoriteEstate(favoriteId);
                dispatch({type: 'auth/SET_FAVORITES', payload: data});
            } else {
                const {data} = await addFavoriteEstate(estate.id);
                dispatch({type: 'auth/SET_FAVORITES', payload: data});
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
        } finally {
            setIsFavoriting(false);
        }
    };

    return (
        <div onClick={onClick} className="cursor-pointer md:border-0 border border-gray-200 rounded-lg overflow-hidden md:rounded-none md:overflow-visible p-3 md:p-0">
            <div className="relative">
                <div className="relative h-72 w-full overflow-hidden rounded-t-md">
                    <img
                        src={getThumbnail()}
                        className="h-full w-full object-cover object-center"
                        alt={getLocalizedTitle()}
                    />
                </div>
                <div className="flex text-xs border rounded-b-md border-gray-200 divide-x divide-gray-200">
                    <div className="flex items-center justify-center flex-1 px-2 py-3 text-gray-500">
                        {estate.bedrooms} rum
                    </div>
                    <div className="items-center justify-center flex-1 hidden px-2 py-3 text-gray-500 sm:flex">
                        {estate.type}
                    </div>
                    <div className="flex items-center justify-center flex-1 px-2 py-3 text-gray-500">
                        {estate.living_area || estate.squareFeet} m<sup>2</sup>
                    </div>
                    <div className="items-center justify-center flex-1 hidden px-2 py-3 text-gray-500 sm:flex">
                        {estate.year_built || 'N/A'}
                    </div>
                </div>

                <div className="relative mt-3">
                    <h3 className="text-lg font-bold text-gray-900 truncate"> {getLocalizedTitle()}</h3>
                    <p className="mt-2 text-sm text-gray-500">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="w-4 h-4 relative mr-2 -top-0.5 inline-block"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                            />
                        </svg>
                        {estate.region?.name || estate.location}
                    </p>
                </div>

                <div
                    className="absolute inset-x-0 top-0 flex h-72 items-end justify-end overflow-hidden rounded-lg p-4">
                    <div
                        aria-hidden="true"
                        className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-black opacity-50"
                    />
                    {isAuthenticated && (
                        <button
                            onClick={handleFavoriteClick}
                            disabled={isFavoriting}
                            className="absolute z-50 top-4 right-4 p-2 rounded-full hover:bg-black/10 transition-colors"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill={isFavorited ? "currentColor" : "none"}
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="size-6 text-white"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"
                                />
                            </svg>
                        </button>
                    )}
                    <p className="relative text-lg font-semibold text-white">{formatPriceSummary(estate.price)}</p>
                </div>
            </div>
        </div>
    );
};

export default SingleEstate;